<template>
  <router-view />
  <IncomingCallModal />
  <CallScreen />
  <NotificationToast />
  <PopupManager />
  <LiveRecoveryBanner />
</template>
<script>
import { onMounted, onUnmounted, nextTick, watch } from 'vue';
import { useRouter } from 'vue-router';
import { useSetting } from './store/pinia'
import { useNotificationSystem } from '@/domains/notifications/composables/useNotifications.v2'  // ✅ UPDATED to v2
import { useMediaSession } from '@/shared/composables/useMediaSession'
import { getAccessTokenToCookies, getAccessToken } from '@/shared/utils/tokenHelper'
import { detectLanguage, getDirectionForLanguage } from './i18n/languageDetector'
import { checkHealth } from '@/shared/services/health'
import { useI18n } from 'vue-i18n';
import NotificationToast from '@/domains/notifications/components/features/NotificationToast.vue'
import PopupManager from '@/shared/components/feedback/Popup/PopupManager.vue'
import IncomingCallModal from '@/domains/chat/components/layouts/FullScreenChat/IncomingCallModal.vue';
import CallScreen from '@/domains/chat/components/layouts/FullScreenChat/CallScreen.vue';
import LiveRecoveryBanner from '@/domains/live/components/LiveRecoveryBanner.vue';
import { useCallStore } from '@/domains/chat/store/callStore';
import { ensureFriendsSocketListeners } from '@/domains/friends/composables/useFriends';

export default {
  name: 'App',
  components: {
    NotificationToast,
    PopupManager,
    IncomingCallModal,
    CallScreen,
    LiveRecoveryBanner,
  },
  setup() {
    const store = useSetting()
    const router = useRouter()
    const { locale } = useI18n()
    const { initialize: initializeNotifications, cleanup: cleanupNotifications } = useNotificationSystem()
    // Initialize secure media session (auto-starts on mount if authenticated)
    useMediaSession()

    // Health check function
    const performHealthCheck = async () => {
      // Skip health check if already on maintenance page
      const currentPath = window.location.pathname;
      if (currentPath.includes('maintenance-mode')) {
        return;
      }

      try {
        const result = await checkHealth();

        if (!result.isHealthy) {
          // API is unhealthy - redirect to maintenance mode
          router.push({ name: 'default.maintenance-mode' });
        }
      } catch (error) {
        // On any error, show maintenance page
        console.error('Health check error:', error);
        router.push({ name: 'default.maintenance-mode' });
      }
    };

    const syncDirectionWithLocale = () => {
      const currentLocale = locale.value || detectLanguage()
      const expectedDirection = getDirectionForLanguage(currentLocale)
      const currentDir = document?.documentElement?.getAttribute('dir') || null
      if (currentDir !== expectedDirection) {
        store.theme_scheme_direction(expectedDirection)
      }
    }

    onMounted(() => {
      // Perform health check first
      performHealthCheck();

      // Defer non-critical operations to next tick to avoid blocking initial render
      store.setSetting()

      // Use nextTick to defer language detection and notifications
      nextTick(() => {
        // Apply direction based on current language
        const currentLanguage = detectLanguage();
        const direction = getDirectionForLanguage(currentLanguage);
        store.theme_scheme_direction(direction);
        syncDirectionWithLocale()

        // Check authentication and initialize notifications asynchronously
        const isUseCookies = process.env.VUE_APP_USE_COOKIES === 'true';
        const token = isUseCookies ? getAccessTokenToCookies() : getAccessToken();

        if (token) {
          // Defer notification initialization to avoid blocking
          setTimeout(() => {
            ensureFriendsSocketListeners();
            initializeNotifications();
            // Initialize global call listeners so incoming calls appear on all pages
            const callStore = useCallStore();
            callStore.init();
          }, 100);
        }
      });
    })

    watch(
      () => locale.value,
      () => {
        syncDirectionWithLocale()
      },
      { immediate: true }
    )

    onUnmounted(() => {
      try {
        useCallStore().stopCallLifecycleSync();
      } catch (_) {
        /* Pinia may be torn down */
      }
      cleanupNotifications();
    })
  }
}
</script>

<style>
/* Main CSS is imported in main.js */
/* Additional plugin CSS */
@import url("./plugins/fullcalendar/core/main.css");
@import url("./plugins/fullcalendar/daygrid/main.css");
@import url("./plugins/fullcalendar/timegrid/main.css");
@import url("./plugins/fullcalendar/list/main.css");
</style>

